import { useState, useRef, useEffect } from "react";
import Modal from 'react-modal';
import { useForm } from "react-hook-form";
import { ModalContainer, Form, Input, InlineFormField, Label, EditFormField, ErrorMessage } from '../../styles/global';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { searchStoreProducts } from "../../services/storeProductService";
import { createStockTransaction, updateStockTransaction } from "../../services/stockTransactionService";
import { updateStoreProduct } from "../../services/storeProductService";
import { getAllSuppliers } from "../../services/supplierService";
import { Loader } from "../../components/Loader";
import { UNITIES } from "../../constants";
import { StoreProductModal } from "../../components/StoreProductModal";
import { SupplierModal } from "../../components/SupplierModal";
import { ISupplier } from "../../interfaces/ISupplier";
import { useSuccessMessage } from "../../contexts/SuccessMessageContext";
import { IStockTransaction as IStockTransactionData } from "../../interfaces/IStockTransaction";

interface StockTransactionModalProps{
    isOpen: boolean;
    onRequestClose: ()=> void;
    loadData: () => void;
    action: string;
    currentTransaction?: IStockTransactionData | null;
}

interface IStockTransaction {
    supplier_id: string;
    supplier: string;
    unity: string;
    quantity: number;
    unity_price: number;
    total_price: number;
    selling_price: number;
    purchased_date: string;
    box_value: number;
    box_unities: number;
}

function getTodayDate() {
    return new Date().toLocaleDateString('en-CA');
}
export function StockTransactionModal({
    isOpen,
    onRequestClose,
    loadData,
    action,
    currentTransaction,
}:StockTransactionModalProps){
    const {
        register,
        handleSubmit,
        setValue,
        setError,
        formState: { errors },
        watch
    } = useForm<IStockTransaction>();
    const [showLoader, setShowLoader] = useState(false);
    const [query, setQuery] = useState('');
    const [productSuggestions, setProductSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [productError, setProductError] = useState('');
    const [productModal, setProductModal] = useState(false);
    const [supplierModal, setSupplierModal] = useState(false);
    const [suppliers, setSuppliers] = useState<ISupplier[]>([]);
    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState<ISupplier | null>(null);
    const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const supplierDropdownRef = useRef<HTMLDivElement>(null);
    const supplierSearchInputRef = useRef<HTMLInputElement>(null);
    const { showSuccess } = useSuccessMessage();

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const handleSearchProducts = (text: string) => {
        setQuery(text);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        
        debounceTimeout.current = setTimeout(async () => {
            if (text.length >= 2) {
                const response = await searchStoreProducts(text);
                setProductSuggestions(response.data as any);
                setShowSuggestions(true);
            }
        }, 700);
    };

    const handleSelectProduct = (product: any) => {
        setSelectedProduct(product);
        setQuery(product.name);
        setShowSuggestions(false);
        setValue("selling_price", product.price || 0);
    };

    const loadSuppliers = async () => {
        try {
            const response = await getAllSuppliers();
            setSuppliers(response.data);
            return response.data as ISupplier[];
        } catch (error) {
            console.error("Erro ao carregar fornecedores:", error);
        }
    };

    const handleSelectSupplier = (supplier: ISupplier) => {
        setSelectedSupplier(supplier);
        setValue("supplier_id", supplier.id);
        setShowSupplierDropdown(false);
        setSupplierSearchQuery('');
    };

    const filteredSuppliers = suppliers.filter(supplier =>
        supplier.name.toLowerCase().includes(supplierSearchQuery.toLowerCase())
    );

    const handleStockTransaction = async (formData: IStockTransaction) => {
        if (formData.quantity === 0 || formData.unity_price === 0 || formData.total_price === 0) {
            if (formData.quantity === 0) {
                setError("quantity", {
                    type: "required",
                    message: "Quantidade é obrigatório",
                });
            } else if (formData.unity_price === 0) {
                setError("unity_price", {
                    type: "required",
                    message: "Preço é obrigatório",
                });
            } else if (formData.total_price === 0) {
                setError("total_price", {
                    type: "required",
                    message: "Total é obrigatório",
                });
            }
            return;
        }

        else if (!selectedProduct) {
            setProductError("Produto é obrigatório");
            return;
        }
        
        else if (!formData.supplier_id) {
            setError("supplier_id", {
                type: "required",
                message: "Fornecedor é obrigatório",
            });
            return;
        }

        try {
            setShowLoader(true);
            const selectedSupplier = suppliers.find(s => s.id === formData.supplier_id);
            const data = {
                store_product_id: selectedProduct.id,
                supplier_id: formData.supplier_id,
                supplier: selectedSupplier?.name || "",
                unity: formData.unity,
                quantity: formData.quantity,
                unity_price: formData.unity_price,
                total_price: formData.total_price,
                purchased_date: formData.purchased_date,
            }

            if (action === "edit") {
                await updateStockTransaction(currentTransaction?.id as string, data);
                showSuccess("Compra atualizada com sucesso!");
            } else {
                await createStockTransaction(data);
                showSuccess("Compra registrada com sucesso!");
            }

            const sellingPrice = Number(formData.selling_price);
            if (sellingPrice !== (selectedProduct.price || 0)) {
                await updateStoreProduct({
                    id: selectedProduct.id,
                    price: sellingPrice,
                });
            }

            setShowLoader(false);
            onRequestClose();
            loadData();
        } catch(error) {
            setShowLoader(false);
        }
    }

    useEffect(() => {
        async function loadSuppliersOnOpen() {
            if (isOpen) {
                const loadedSuppliers = await loadSuppliers();

                if (currentTransaction) {
                    const supplier = loadedSuppliers?.find((s: ISupplier) => s.id === currentTransaction.supplier_id);
                    if (supplier) {
                        setSelectedSupplier(supplier);
                    }
                }
            }
        }

        loadSuppliersOnOpen();

        if (currentTransaction) {
            setValue("supplier_id", currentTransaction.supplier_id);
            setValue("supplier", currentTransaction.supplier);
            setValue("unity", currentTransaction.unity);
            setValue("quantity", currentTransaction.quantity);
            setValue("unity_price", currentTransaction.unity_price);
            setValue("total_price", currentTransaction.total_price);
            setValue("purchased_date", action === "edit" ? currentTransaction.purchased_date?.slice(0, 10) : getTodayDate());
            setValue("box_value", 0);
            setValue("box_unities", 0);

            const storeProduct = currentTransaction.storeProduct;
            setSelectedProduct(storeProduct ? {
                id: currentTransaction.store_product_id,
                name: storeProduct.product?.name,
                price: storeProduct.price,
            } : null);
            setQuery(storeProduct?.product?.name || "");
            setValue("selling_price", storeProduct?.price || 0);
            setSelectedSupplier(null);
        } else {
            setValue("supplier_id", "");
            setValue("supplier", "");
            setValue("unity", "");
            setValue("quantity", 0);
            setValue("unity_price", 0);
            setValue("total_price", 0);
            setValue("selling_price", 0);
            setValue("purchased_date", getTodayDate());
            setSelectedProduct(null);
            setSelectedSupplier(null);
            setValue("box_value", 0);
            setValue("box_unities", 0);
        }
        setShowSupplierDropdown(false);
        setSupplierSearchQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    useEffect(() => {
        if (watch("quantity") > 0 && watch("unity_price") > 0 && watch("unity") !== "CX") {
            setValue("total_price", watch("quantity") * watch("unity_price"));
        } else if (watch("box_unities") > 0 && watch("box_value") > 0 && watch("unity") === "CX") {
            setValue("total_price", Number(watch("quantity") * watch("box_value")));
            setValue("unity_price",  Number((watch("box_value") / watch("box_unities")).toFixed(2)))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watch("quantity"), watch("unity_price"), watch("box_unities"), watch("box_value")],);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setShowSuggestions(false);
            }
            if (
                supplierDropdownRef.current &&
                !supplierDropdownRef.current.contains(event.target as Node)
            ) {
                setShowSupplierDropdown(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (showSupplierDropdown && supplierSearchInputRef.current) {
            supplierSearchInputRef.current.focus();
        }
    }, [showSupplierDropdown]);

    return(
        <Modal 
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            overlayClassName="react-modal-overlay"
            className="react-modal-content"
        >
            <Loader show={showLoader} />
            <button type="button" onClick={onRequestClose} className="modal-close">
                <FontAwesomeIcon icon={faXmark}/>
            </button>

            <ModalContainer>
                <Form onSubmit={handleSubmit(handleStockTransaction)}>
                    <h2>{action === "create" ? "Nova" : "Editar"} compra</h2>
                    <div style={{ position: 'relative', width: '100%' }} ref={wrapperRef}>
                        <Label>Produto<span>*</span></Label>
                        <Input
                            placeholder="Produto"
                            value={query}
                            onChange={(e) => handleSearchProducts(e.target.value)}
                            onFocus={() => query && setShowSuggestions(true)}
                        />

                        {showSuggestions && productSuggestions.length > 0 && query.length >= 2 && (
                            <ul className="suggestion-box">
                            {productSuggestions.map((product: any) => (
                                <li key={product.id} onClick={() => handleSelectProduct(product)}>
                                {product.name} - R$ {product.price}
                                </li>
                            ))}
                            </ul>
                        )}
                        {productError && <ErrorMessage>{productError}</ErrorMessage>}
                    </div>
                    <button
                        className="new-product-button"
                        type="button"
                        onClick={() => setProductModal(true)}>Novo produto</button>
                    <InlineFormField fullWidth>
                        <EditFormField removeMarginBottom>
                            <Label>Fornecedor<span>*</span></Label>
                            <div className="custom-select-container" ref={supplierDropdownRef}>
                                <input type="hidden" {...register("supplier_id", { required: "Fornecedor é obrigatório" })} />
                                <button
                                    type="button"
                                    className={`custom-select-button ${!selectedSupplier ? 'placeholder' : ''}`}
                                    onClick={() => setShowSupplierDropdown(!showSupplierDropdown)}
                                >
                                    <span>{selectedSupplier ? selectedSupplier.name : 'Selecione o fornecedor'}</span>
                                    <span style={{ fontSize: '12px' }}>▼</span>
                                </button>
                                {showSupplierDropdown && (
                                    <div className="custom-select-dropdown">
                                        <div className="custom-select-search">
                                            <input
                                                ref={supplierSearchInputRef}
                                                type="text"
                                                placeholder="Buscar fornecedor..."
                                                value={supplierSearchQuery}
                                                onChange={(e) => setSupplierSearchQuery(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <ul className="custom-select-list">
                                            {filteredSuppliers.length > 0 ? (
                                                filteredSuppliers.map((supplier) => (
                                                    <li
                                                        key={supplier.id}
                                                        onClick={() => handleSelectSupplier(supplier)}
                                                        className={selectedSupplier?.id === supplier.id ? 'selected' : ''}
                                                    >
                                                        {supplier.name}
                                                    </li>
                                                ))
                                            ) : (
                                                <li className="no-results">Nenhum fornecedor encontrado</li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            {errors.supplier_id && <ErrorMessage>{errors.supplier_id.message}</ErrorMessage>}
                        </EditFormField>
                        <EditFormField removeMarginBottom>
                            <Label>Data de compra<span>*</span></Label>
                            <Input type="date" {...register("purchased_date", {
								required: "Data de entrega é obrigatória",
							})} />
                            {errors.purchased_date && <ErrorMessage>{errors.purchased_date.message}</ErrorMessage>}
                        </EditFormField>
                    </InlineFormField>
                    <button
                        className="new-supplier-button"
                        type="button"
                        onClick={() => setSupplierModal(true)}>Novo fornecedor</button>

                    <InlineFormField fullWidth>
                        <EditFormField>
                            <Label>Unidade<span>*</span></Label>
                            <select placeholder='Unidade' {...register("unity", { required: "Unidade invária" })} style={{ height: "4rem" }}>
                                <option value="">Selecione a unidade</option>
                                {Object.entries(UNITIES).map(([key, value]) => (
                                    <option key={key} value={key}>{value}</option>
                                ))}
                            </select>
                            {errors.unity && <ErrorMessage>{errors.unity.message}</ErrorMessage>}
                        </EditFormField>
                        <EditFormField>
                            <Label>Quantidade<span>*</span></Label>
                            <input type="number"
                                {...register("quantity", { required: "Quantidade é obrigatória" })}/>
                            {errors.quantity && <ErrorMessage>{errors.quantity.message}</ErrorMessage>}
                        </EditFormField>
                    </InlineFormField>
                    {watch("unity") === "CX" && 
                        <InlineFormField fullWidth>
                            <EditFormField>
                                <Label>Preço por caixa<span>*</span></Label>
                                <input type="number" step={0.01}
                                    {...register("box_value", { required: "Preço por caixa é obrigatório" })}/>
                                {errors.unity_price && <ErrorMessage>{errors.unity_price.message}</ErrorMessage>}
                            </EditFormField>
                            <EditFormField>
                                <Label>Unidades por caixa<span>*</span></Label>
                                <input type="number" step={0.01}
                                    {...register("box_unities", { required: "Unidades por caixa é obrigatório" })}/>
                                {errors.total_price && <ErrorMessage>{errors.total_price.message}</ErrorMessage>}
                            </EditFormField>
                        </InlineFormField>
                    }
                    <InlineFormField fullWidth>
                        <EditFormField>
                            <Label>Preço por unidade<span>*</span></Label>
                            <input type="number" step={0.01}
                                {...register("unity_price", { required: "Preço por unidade é obrigatório" })}/>
                            {errors.unity_price && <ErrorMessage>{errors.unity_price.message}</ErrorMessage>}
                        </EditFormField>
                        <EditFormField>
                            <Label>Preço total<span>*</span></Label>
                            <input type="number" step={0.01}
                                {...register("total_price", { required: "Preço total é obrigatório" })}/>
                            {errors.total_price && <ErrorMessage>{errors.total_price.message}</ErrorMessage>}
                        </EditFormField>
                    </InlineFormField>
                    <EditFormField>
                        <Label>Preço de Venda<span>*</span></Label>
                        <input type="number" step={0.01}
                            {...register("selling_price", { required: "Preço de venda é obrigatório" })}/>
                        {errors.selling_price && <ErrorMessage>{errors.selling_price.message}</ErrorMessage>}
                        <small style={{ color: '#666', display: 'block', marginTop: '0.3rem' }}>
                            Ao alterar este valor, o preço de venda do produto nesta loja também será atualizado.
                        </small>
                    </EditFormField>
                    <button type="submit" className="create-button">
                        {action === "create" ? "Criar" : "Editar"}
                    </button>
                </Form>
            </ModalContainer>
            <StoreProductModal 
                isOpen={productModal}
                onRequestClose={() => setProductModal(false)}
                loadData={async (storeId: string) => {
                    // Não precisa fazer nada aqui pois produtos serão recarregados automaticamente
                    // quando necessário nas páginas que listam produtos
                }}
                action={"create"}
                currentProduct={{
                    id: "",
                    name: "",
                    price: 0,
                    unity: "",
                    stock: 0,
                    enabled: false
                }}
            />
            <SupplierModal 
                isOpen={supplierModal}
                onRequestClose={() => setSupplierModal(false)}
                loadData={loadSuppliers}
            />
        </Modal>
    )
}
